import type { FieldErrors } from "react-hook-form";
import type { RefinanceDebt } from "@/features/refinance/types";
import { isCommittedDebt, sumCommittedDebts } from "./debt-status";
import {
	type RefinanceOfferParts,
	findRefinanceDebtIssues,
	resolveRefinanceAllocation,
} from "./refinance-allocation";

interface BlockedRefinanceInput {
	offer: RefinanceOfferParts | null | undefined;
	appliedRefinance: number;
	debts: RefinanceDebt[];
	debtErrors: FieldErrors<{ debts: RefinanceDebt[] }>["debts"];
}

/**
 * Why a refinance submit was blocked: the totals rules that failed, the figures
 * they compared, and each debt's invalid fields. Amounts and flags only, never
 * a lender, IBAN or reference.
 */
export const describeBlockedRefinance = ({
	offer,
	appliedRefinance,
	debts,
	debtErrors,
}: BlockedRefinanceInput) => {
	const { mandatory, internal, collected, maxLoanAmount, excess } =
		resolveRefinanceAllocation({
			offer,
			appliedRefinance,
			collected: sumCommittedDebts(debts),
		});

	return {
		totalsIssues: findRefinanceDebtIssues({ offer, appliedRefinance, debts }),
		required: mandatory.capacity,
		requiredRemaining: mandatory.remaining,
		existingLoan: internal,
		collected,
		maxLoanAmount,
		excess,
		debts: debts.map((debt, index) => ({
			index,
			committed: isCommittedDebt(debt),
			reported: debt.isReported === true,
			fromRegister: debt.fromRegister === true,
			internalRedemption: debt.internalRedemption === true,
			amount: debt.amount ?? null,
			originalAmount: debt.originalAmount ?? null,
			invalidFields: Object.keys(debtErrors?.[index] ?? {}),
		})),
	};
};
